import { ZButton } from 'components/ZButton';
import { ZIcon } from 'components/ZIcon';
import { ZLabel } from 'components/ZLabel';
import { ZNumberInput } from 'components/ZNumberInput';
import { ZSingleSelectDropdown } from 'components/ZSingleSelectDropdown';
import { ZTextInput } from 'components/ZTextInput';
import { ZTooltip } from 'components/ZTooltip';
import { States, statesToString } from 'constants/constants';
import { ZIconType } from 'helpers/utility/iconMap';
import { ManagedStockView } from 'models/dbModels/managedStock/ManagedStock';
import { ManagedStockPde } from 'models/dbModels/managedStock/ManagedStockPde';
import { ManagedSupplier } from 'models/dbModels/managedSuppliers/ManagedSupplier';
import { ManagedApnPdeType } from 'models/dbModels/pdes/CombinedPdes';
import { useEffect, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { importFromProductsWithData, linkToProduct } from 'redux/slices/Stocks';
import { getSuppliers } from 'redux/slices/Suppliers';
import { RootState } from 'redux/store';
import { ManagedStockMerge } from '../../ManagedStockMerge';
import { Link } from 'react-router-dom';
import { ErrorCase, ErrorHandler, ErrorInformation, getErrorCase, getModelDataForImport } from './ErrorHandler';
import { CheckDuplicateApnPde } from 'redux/slices/ApnPde';

type PdeFormProps = {
	selectedPde?: ManagedStockPde;
	pdes?: ManagedStockPde[];
	stockModel: ManagedStockView;
	formErrors: FieldErrors<ManagedStockView>;
	showPopup?: boolean;
	onHide?: () => void;
	onSubmit?: (pde: ManagedStockPde) => void;
}

type ValidationFormProps = Partial<ManagedStockPde> & {
	pde: string;
	states: States;
	supplierSelection: ManagedSupplier | null;
}

export default function PDE({ selectedPde, pdes, showPopup, onHide, onSubmit, stockModel, formErrors }: PdeFormProps) {
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const navigate = useNavigate();
	const defaultValues: ValidationFormProps = {
		supplierSelection: null,
		pde: "",
		states: States.ALL,
		ws1Cost: null,
		cartonSize: null,
		moq: null,
	}
	const { 
		handleSubmit, 
		control,
		reset,
		getValues,
		setError,
		formState: { errors },
	} = useForm<ValidationFormProps>({
		defaultValues: defaultValues,
		mode: "onChange",
		reValidateMode: "onChange",
	})
	const dispatch = useAppDispatch();
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const initialFormPde: ManagedStockPde = {
		id: 0,
		moq: null,
		ws1Cost: null,
		cartonSize: null,
		pde: "",
		supplierId: 0,
		states: States.ALL,
		isSystem: false,
	}

	const [formPde, setFormPde] = useState<ManagedStockPde>(initialFormPde);
	const [mode, setMode] = useState<"add" | "edit">("add");
	const [prevPde, setPrevPde] = useState<string>("");

	const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
	const [showManualMergePopup, setShowManualMergePopup] = useState<boolean>(false);
	const [mergeStockIds, setMergeStockIds] = useState<number[]>([]);

	const [pdeErrorInfo, setPdeErrorInfo] = useState<ErrorInformation>();

	useEffect(() => {
		if (showPopup) {
			if (selectedPde != null) {
				setFormPde(selectedPde);
				setPrevPde(selectedPde.pde);
				reset({
					supplierSelection: (suppliers.table as any[]).find(s => s.id === selectedPde.supplierId) as ManagedSupplier | null,
					pde: selectedPde.pde,
					ws1Cost: selectedPde.ws1Cost,
					cartonSize: selectedPde.cartonSize,
					moq: selectedPde.moq,
					states: selectedPde.states,
				} as ValidationFormProps);
				setMode("edit");
			} else {
				resetFormPde();
				setMode("add");
			}

			if (selectedPde && selectedPde.isSystem) {
				// Autofocus on carton size since other fields are disabled, we can't use autofocus reliably because we are hiding/unhiding the popup
				document.getElementById("carton-size")?.focus(); 
			} else {
				// Autofocus on pde since other fields are disabled, we can't use autofocus reliably because we are hiding/unhiding the popup
				document.getElementById("pde")?.focus();
			}
		}
	}, [showPopup]);

	async function handleFormSubmit() {
		handleSubmit(async (validData) => {
			setSubmitting(true);
			// Validation success
			const formValues = getValues();
			const isDuplicate = await isDuplicatePde(formValues.pde, formValues.supplierSelection!.id);

			setSubmitting(false);
			if (isDuplicate) {
				setError("pde", { type: "custom", message: `This PDE already exists for supplier ${formValues.supplierSelection!.name}`});
			} else {
				// Success! Let's save the PDE...
				onSubmit && onSubmit({
					...formPde,
					...formValues,
					supplierId: formValues.supplierSelection!.id,
				});
				resetFormPde();
			}
		},
		(invalidData) => {
			// Validation fail
		})();
	}

	function handleFormCancel() {
		resetFormPde();
		onHide && onHide();
	}

	async function isDuplicatePde(pde: string, supplierId: number): Promise<boolean> {
		if (pde === prevPde) return false;
		// Early check against existing pdes in the table on the managed stock.
		if (pdes?.some(p => p.pde === pde && p.supplierId === supplierId)) return true;

		let isDupe = false;

		await dispatch(CheckDuplicateApnPde({
			filters: [{
				pde: pde,
				supplierId: supplierId
			}],
			queryType: 'All'
		}))
		.then((res) => {
			if (res.data && res.data.length !== 0) {
				// Common data:
				// 	- stockModel
				//	- dupe structure
				//  - cases are triggered the same way
				// We have duplicates
				// Set up error variables
				const dupe = res.data[0]; // We shouldn't be getting multiple from this endpoint
				const supplierName = (suppliers.table.find((s: ManagedSupplier) => s.id === supplierId) as ManagedSupplier | undefined)?.name ?? "Unknown";
				let errorInfo: ErrorInformation | undefined = undefined;
				const errorCase = getErrorCase(dupe, stockModel);
				// There are several cases we have to consider

				switch(errorCase) {
					case ErrorCase.UpdateSystem_System:
					case ErrorCase.New_System:
						const subset = getModelDataForImport(formErrors, stockModel);
						errorInfo = {
							heading: "PDE already exists on system stock",
							messages: [
								`The system stock ${dupe.parentModelName} already exists with the PDE ${pde} for ${supplierName}.`,
								"Would you like to import it now?"
							],
							buttonInfo: {
								buttonText: "Import",
								loadingButtonText: "Importing...",
								buttonAction: async () => {
									setLoading(true);
									const response = await dispatch(importFromProductsWithData([
										{ ...subset, productId: dupe.parentId }
									]));
									// We expect back a list of ids corresponding to the created managed stocks, since we are sending a single import, we expect a single id back
									// Use this id to navigate
									setLoading(false);
									handleFormCancel();
									navigate(`/app/stock/${response.data[0]}`);
								},
								loading: loading
							}
						}
						break;
					case ErrorCase.New_SystemManaged:
					case ErrorCase.New_CustomManaged:
						errorInfo = {
							heading: "PDE already exists on managed stock",
							messages: [
								`A managed stock already exists with PDE ${pde} for ${supplierName}.`,
								"Would you like to navigate there now?"
							],
							link: `/app/stock/${dupe.parentId}`,
							buttonInfo: {
								buttonText: "Go to stock",
								buttonAction: () => {
									handleFormCancel();
									navigate(`/app/stock/${dupe.parentId}`);
								}
							}
						}
						break;
					case ErrorCase.UpdateSystem_SystemManaged:
						errorInfo = {
							heading: "PDE already exists on managed stock",
							messages: [
								`A managed stock already exists with PDE ${pde} for ${supplierName}.`,
								"Would you like to navigate there now?"
							],
							link: `/app/stock/${dupe.parentId}`,
							buttonInfo: {
								buttonText: "Go to stock",
								buttonAction: () => {
									handleFormCancel();
									navigate(`/app/stock/${dupe.parentId}`);
								}
							}
						}
						break;
					case ErrorCase.UpdateSystem_CustomManaged:
						errorInfo = {
							heading: "PDE already exists on managed stock",
							messages: [
								`A managed stock already exists with PDE ${pde} for ${supplierName}.`,
								"Would you like to merge your existing managed stock to this one?"
							],
							buttonInfo: {
								buttonText: "Merge",
								loadingButtonText: "Merging...",
								buttonAction: () => {
									setLoading(true);
									setMergeStockIds([stockModel.id, dupe.parentId]);
									setShowManualMergePopup(true);
								},
								loading: loading
							}
						}
						break;
					case ErrorCase.UpdateCustom_System:
						errorInfo = {
							heading: "PDE already exists on system stock",
							messages: [
								`A system stock already exists with PDE ${pde} for ${supplierName}.`,
								"Would you like to link your existing managed stock to this system one?"
							],
							buttonInfo: {
								buttonText: "Link",
								loadingButtonText: "Linking...",
								buttonAction: () => {
									setLoading(true);
									dispatch(linkToProduct([
										{
											productId: dupe.parentId,
											managedId: stockModel.id
										}
									])).then(() => {
										setLoading(false);
										handleFormCancel();
										navigate(`/app/stock/${stockModel.id}`);
									});
								},
								loading: loading
							}
						}
						break;
					case ErrorCase.UpdateCustom_SystemManaged:
					case ErrorCase.UpdateCustom_CustomManaged:
						errorInfo = {
							heading: "PDE already exists on managed stock",
							messages: [
								`A managed stock already exists with PDE ${pde} for ${supplierName}.`,
								"Would you like to merge your existing managed stock to this one?"
							],
							buttonInfo: {
								buttonText: "Merge",
								loadingButtonText: "Merging...",
								buttonAction: () => {
									setLoading(true);
									setMergeStockIds([stockModel.id, dupe.parentId]);
									setShowManualMergePopup(true);
								},
								loading: loading
							}
						}
						break;
				}
				
				setPdeErrorInfo(errorInfo);
				setShowErrorPopup(true);
				isDupe = true;
			}
		 });

		 return isDupe;
	}

	const RequiredComponent = (text: string, tooltipText: string) => {
		return (
			<ZLabel>
				<span className="d-inline-flex align-items-center gap-1 w-100">
					{text}
					<ZTooltip enabled placement='top' text={tooltipText}>
						<>*</>
					</ZTooltip>
				</span>
			</ZLabel>
		);
	}

	function resetFormPde() {
		setMode("add");
		reset(defaultValues);
		setFormPde(initialFormPde);
		setPdeErrorInfo(undefined);
		setShowErrorPopup(false);
		setShowManualMergePopup(false);
		setMergeStockIds([]);
	}

	function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			e.stopPropagation();
			e.preventDefault();
		}
	}

	return (
		<>
		{ showManualMergePopup && <ManagedStockMerge
			loadStocks
			show={showManualMergePopup}
			stockIds={mergeStockIds}
			handleClose={() => setShowManualMergePopup(false)}
			afterMerge={() => {
				// TODO: Nav to newly merged stock??
				setLoading(false);
				handleFormCancel();
				// TODO: This is maybe not the right id
				navigate(`/app/stock/${stockModel.id}`);
			}}
		/>}
		{!showManualMergePopup && <ErrorHandler errorInfo={pdeErrorInfo} showError={showErrorPopup} onBack={() => setShowErrorPopup(false)} />}
		{!showErrorPopup && !showManualMergePopup && <Modal
			size="lg"
			centered
			backdrop="static"
			show={showPopup}
			onHide={handleFormCancel}
		>
			<Modal.Header closeButton>
				<Modal.Title>PDE</Modal.Title>
			</Modal.Header>
			<Modal.Body onKeyDown={handleKeyPress}>
				<Row className="mb-1">
					<Col>
							<Row>
								<p className="text-muted">Fill the form below in order to {mode === "add" ? 'create a new PDE.' : 'edit the selected PDE.'}</p>
								<Col md={6} className="mt-2">
									<Controller 
										control={control}
										rules={{
											required: { value: true, message: "Please enter a PDE." },
										}}
										render={({ field: { onChange, onBlur, value } }) => 
											<ZTextInput
												id="pde"
												disabled={formPde.isSystem}
												label={RequiredComponent("PDE", "This field is required")}
												placeholder="Enter a PDE"
												value={value}
												validation={
													{ 
														invalid: errors.pde != null,
														error: errors.pde?.message
													}
												}
												onChange={onChange}
												onBlur={onBlur}
											/>
										}
										name={'pde'}
									/>
								</Col>
								<Col md={6} className="mt-2">
									<Controller 
										control={control}
										rules={{
											required: { value: true, message: "Please select a supplier." }
										}}
										render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
											return <ZSingleSelectDropdown
												dropdownIsDisabled={formPde.isSystem}
												label={RequiredComponent("Supplier", "This field is required")}
												options={suppliers.table}
												value={value}
												validation={
													{ 
														invalid: errors.supplierSelection != null,
														error: errors.supplierSelection?.message
													}
												}
												onBlur={onBlur}
												optionKey={(opt) => opt?.id}
												disabled={() => false}
												barSize={"compact"}
												display={(opt) => opt?.name ?? "Unknown"}
												onSelectionChange={(newSupplier) => {
													newSupplier && onChange(newSupplier);
												}} 
												hasSearch
											/>
										}}
										name={'supplierSelection'}
									/>
								</Col>
							</Row>
							<Row>
								<Col md={6} className="mt-2">
									{/** TODO: This needs to be changed to a multi-select with some proper logic, right now the value isn't really handled properly, we just want to display it for now */}
									<Controller 
										control={control}
										render={({ field: { onChange, onBlur, value } }) => 
											<ZTextInput
												disabled={true}
												label={<ZLabel>State</ZLabel>}
												placeholder="Enter a state"
												value={statesToString(value)}
												validation={
													{ 
														invalid: errors.states != null,
														error: errors.states?.message
													}
												}
												onChange={onChange}
												onBlur={onBlur}
											/>
										}
										name={'states'}
									/>
								</Col>
								<Col md={6} className="mt-2">
									<Controller 
										control={control}
										render={({ field: { onChange, onBlur, value } }) => 
											<ZNumberInput
												id="ws1"
												label={<ZLabel>WS1</ZLabel>}
												placeholder="Enter a WS1"
												type="price"
												disabled={formPde.isSystem}
												value={value ?? undefined}
												validation={
													{ 
														invalid: errors.ws1Cost != null,
														error: errors.ws1Cost?.message
													}
												}
												onChange={onChange}
												onBlur={onBlur}
											/>
										}
										name='ws1Cost'
									/>
								</Col>
							</Row>
							<Row>
								<Col md={6} className="mt-2">
									<Controller 
										control={control}
										rules={{
											min: {
												value: 0,
												message: "Carton size must not be negative"
											},
											max: {
												value: 999,
												message: "Max carton size of 999 exceeded"
											}
										}}
										render={({ field: { onChange, onBlur, value } }) => 
											<ZNumberInput
												id="carton-size"
												label={<ZLabel>Carton Size</ZLabel>}
												placeholder="Enter a carton size"
												type="qty"
												value={value ?? undefined}
												validation={
													{ 
														invalid: errors.cartonSize != null,
														error: errors.cartonSize?.message
													}
												}
												onChange={onChange}
												onBlur={onBlur}
											/>
										}
										name='cartonSize'
									/>
								</Col>
								<Col md={6} className="mt-2">
									<Controller 
										control={control}
										rules={{
											min: {
												value: 0,
												message: "Minimum order quantity must not be negative"
											},
											max: {
												value: 999,
												message: "Max minimum order quantity of 999 exceeded"
											}
										}}
										render={({ field: { onChange, onBlur, value } }) => 
											<ZNumberInput
												type="qty"
												step={1}
												label={<ZLabel>Minimum Order Quantity (MOQ)</ZLabel>}
												placeholder="Enter a minimum order quantity"
												value={value ?? undefined}
												validation={
													{ 
														invalid: errors.moq != null,
														error: errors.moq?.message
													}
												}
												onChange={onChange}
												onBlur={onBlur}
											/>
										}
										name='moq'
									/>
								</Col>
								</Row>
								<Row className='mt-3'>
									<Col sm={6}>
									</Col>
									<Col sm={6} className="d-flex flex-row-reverse gap-2">
										<ZButton className='cta-button' loading={submitting} variant="primary" buttonText={submitting ? `Saving...` : `Save`} onClick={handleFormSubmit} />
										<ZButton className='cta-button' variant="light" buttonText='Cancel' onClick={handleFormCancel} />
									</Col>
								</Row>
							</Col>
						</Row>
					</Modal.Body>
				</Modal>}</>
)}